import { useEffect, useRef } from "react"
import { useTheme } from "next-themes"
import { Toaster as Sonner, toast, useSonner } from "sonner"
import { CircleCheck, CircleX, TriangleAlert, Info } from "lucide-react"
import { useHayBarra, arribaDeLosAvisos, AIRE_DE_LOS_AVISOS_PX } from "../../lib/medidasDelChasis"
import { useMedia, CONSULTA_ANCHO } from "../../lib/useEsAncho"
import { COLOR_DEL_ICONO } from "../../lib/bannerDeLaCasa"

// dashboard-rediseno (firma 4 y componente "Aviso flotante"): los avisos de TODA la app salen arriba al centro,
// gris oscuro (fondo #1F1F1F, borde #333333, radio 14, alto minimo 48, texto 14) y el color va SOLO en el icono de
// 18: verde si salio bien, rojo si fallo, naranja suave si salio a medias, gris claro en los neutros. Sin los fondos
// de color de antes (`richColors` ya no se enciende en App.js).
//
// Donde sale: 16 px debajo de la barra de arriba donde la hay (el chasis la anuncia, ver `lib/medidasDelChasis.js`)
// y 16 px desde arriba donde no (entrar, registrarse). Siempre mas la zona segura del iPhone, como antes. En el
// telefono va a lo ancho con 16 px por lado. Duraciones, apilado, botones de accion y cierre: los de siempre.

const LADOS = `${AIRE_DE_LOS_AVISOS_PX}px`

// El aspecto se pone con las variables de sonner (su hoja lee de ahi el fondo, el borde, el texto y el radio), asi
// que no se estrena ni una clase de la hoja y la conducta de sonner queda intacta.
const VARIABLES_DEL_AVISO = {
  "--normal-bg": "#1F1F1F",
  "--normal-border": "#333333",
  "--normal-text": "#F4F4F3",
  "--border-radius": "14px",
}

const ESTILO_DEL_AVISO = { minHeight: 48, fontSize: 14 }

// camara-pills remate (firma #719): CON BARRA, NINGUN AVISO SE ENCIMA CON ELLA, NI AL ENTRAR. Medido en Chrome (app y
// web, 393 a 1440): sonner si mueve los avisos vivos cuando aparece o se va la barra; lo que cruzaba la barra era la
// ENTRADA: cada aviso aparece deslizandose desde un alto entero mas arriba de su lugar (`translateY(-100%)`), o sea por
// encima de la barra, y dibujado sobre ella (su capa va encima de todo) durante unos 200 a 400 ms; en el iPhone, con la
// zona segura, mas. Con barra, la lista se recorta en su propio borde de arriba (16 px debajo de la barra): el aviso
// sale de debajo de la barra. Sin barra nada cambia. Los lados y el fondo no se recortan (la sombra, el gesto).
export const RECORTE_DEBAJO_DE_LA_BARRA = "inset(0px -100vw -100vh -100vw)"

const icono = (Icono, color) => (
  <Icono aria-hidden="true" style={{ width: 18, height: 18, color, flexShrink: 0 }} />
)

export const ICONOS_DE_LOS_AVISOS = {
  success: icono(CircleCheck, COLOR_DEL_ICONO.exito),
  error: icono(CircleX, COLOR_DEL_ICONO.error),
  warning: icono(TriangleAlert, COLOR_DEL_ICONO.aviso),
  info: icono(Info, COLOR_DEL_ICONO.neutro),
}

// smoke-alumno (S5): LOS AVISOS SE VAN SOLOS Y NO SE AMONTONAN, en toda la app y desde esta puerta.
//
// Por que se quedaban pegados en el iPhone (sonner 2.0.7): el reloj de cada aviso se PAUSA mientras la lista esta
// "expandida" o "en interaccion". La lista se expande con mouseenter y mousemove, que el iPhone emite al tocar un
// aviso, y solo se recoge con mouseleave, que un dedo no produce; la interaccion se prende al bajar el puntero y solo
// se apaga al subirlo (no con pointercancel). Con eso todos los relojes quedaban parados. Y sonner pinta 3 y ENCOLA el
// resto escondido detras: al cerrar los visibles, salian los de atras. Lo que se hace aqui:
//   1. Cada aviso lleva un reloj PROPIO, con su duracion de siempre (la suya, o la de sonner), que no se pausa por el
//      raton ni por el dedo. Un aviso sin fin (`Infinity`) o de carga no lleva reloj.
//      mezcla (21-oct, firma de Carlos): TAMPOCO se pausa con la pestana o la app escondidas. Al aparecer, el aviso
//      recibe su hora de vencimiento (`vence`, ahora mas su duracion) y el reloj se arma a lo que falte. Al volver a
//      la vista (y en `pageshow`, por si la app vuelve congelada y su `setTimeout` no disparo) se quita en ese
//      instante todo aviso ya vencido, y a los demas se les re-arma lo que les falte.
//   2. El mismo aviso no se apila: `toast.success`, `.error`, `.info`, `.warning` y `.message` sin `id` reciben uno
//      sacado de su texto, asi el repetido REEMPLAZA al que esta en su lugar (como ya hacian los de la casa con
//      `id`). Para `toast(...)` a secas, que no se puede envolver, el repetido saca al de antes.
//   3. Como mucho MAXIMO_DE_AVISOS a la vez: si llega otro, el mas viejo de los que tienen reloj se quita en ese
//      momento, asi nunca queda nada esperando detras.

export const MAXIMO_DE_AVISOS = 3
// La de sonner 2.0.7 (`TOAST_LIFETIME`) cuando el aviso no trae la suya.
export const DURACION_DE_SIEMPRE_MS = 4000

const METODOS_CON_TEXTO = ["success", "error", "info", "warning", "message"]
const MARCA_DE_ENVUELTO = "__smkIdPorTexto"

const textoDe = (valor) => (typeof valor === "string" || typeof valor === "number" ? String(valor) : null)

// La identidad de un aviso por lo que dice; null si su texto no es texto (un componente, por ejemplo).
export function llaveDelAviso(tipo, titulo, descripcion) {
  const texto = textoDe(titulo)
  if (texto === null) return null
  return `aviso:${tipo || "normal"}:${texto}:${textoDe(descripcion) || ""}`
}

// Solo sobre el `toast` real de sonner: un doble de pruebas (sin `getHistory`) se deja como esta.
function envolverMetodos(objeto) {
  if (!objeto || typeof objeto.getHistory !== "function") return
  for (const tipo of METODOS_CON_TEXTO) {
    const original = objeto[tipo]
    if (typeof original !== "function" || original[MARCA_DE_ENVUELTO]) continue
    const envuelto = (mensaje, datos) => {
      if (datos && datos.id !== undefined && datos.id !== null && datos.id !== "") return original(mensaje, datos)
      const llave = llaveDelAviso(tipo, mensaje, datos && datos.description)
      return llave ? original(mensaje, { ...(datos || {}), id: llave }) : original(mensaje, datos)
    }
    envuelto[MARCA_DE_ENVUELTO] = true
    objeto[tipo] = envuelto
  }
}
envolverMetodos(toast)

const sinFin = (t, duracion) => t.type === "loading" || Boolean(t.promise) || duracion === Infinity

// Lo que hay que quitar ya: los repetidos de antes (por su texto) y, pasando del maximo, los mas viejos con reloj.
// `vivos` va del mas nuevo al mas viejo (el orden de `useSonner`).
export function avisosQueSobran(vivos, duracionDe) {
  const fuera = new Set()
  const vistas = new Set()
  for (const t of vivos) {
    const llave = llaveDelAviso(t.type, t.title, t.description)
    if (llave === null) continue
    if (vistas.has(llave)) fuera.add(t.id)
    else vistas.add(llave)
  }
  const quedan = vivos.filter((t) => !fuera.has(t.id))
  let sobran = quedan.length - MAXIMO_DE_AVISOS
  for (let i = quedan.length - 1; i >= 0 && sobran > 0; i -= 1) {
    if (sinFin(quedan[i], duracionDe(quedan[i]))) continue
    fuera.add(quedan[i].id)
    sobran -= 1
  }
  return [...fuera]
}

function useGuardiaDeAvisos(duracionDelToaster) {
  const { toasts } = useSonner()
  const relojes = useRef(new Map())

  const duracionDe = (t) => {
    if (typeof t.duration === "number") return t.duration
    if (typeof duracionDelToaster === "number") return duracionDelToaster
    return DURACION_DE_SIEMPRE_MS
  }

  useEffect(() => {
    const vivos = toasts.filter((t) => !t.dismiss && !t.delete)
    for (const id of avisosQueSobran(vivos, duracionDe)) toast.dismiss(id)

    const mapa = relojes.current
    const presentes = new Set(vivos.map((t) => t.id))
    for (const [id, reloj] of mapa) {
      if (!presentes.has(id)) { clearTimeout(reloj.timeout); mapa.delete(id) }
    }
    for (const t of vivos) {
      const duracion = duracionDe(t)
      const previo = mapa.get(t.id)
      if (previo && previo.aviso === t) continue
      if (previo) clearTimeout(previo.timeout)
      if (sinFin(t, duracion)) { mapa.delete(t.id); continue }
      const reloj = { aviso: t, vence: Date.now() + duracion, timeout: null }
      mapa.set(t.id, reloj)
      armar(t.id, reloj)
    }
  })

  useEffect(() => {
    const mapa = relojes.current
    // Al volver: lo vencido se va YA; lo demas se re-arma a lo que le falte.
    const alVolver = () => {
      if (document.hidden) return
      const ahora = Date.now()
      for (const [id, reloj] of mapa) {
        clearTimeout(reloj.timeout)
        reloj.timeout = null
        if (reloj.vence <= ahora) {
          mapa.delete(id)
          toast.dismiss(id)
        } else {
          armar(id, reloj)
        }
      }
    }
    document.addEventListener("visibilitychange", alVolver)
    window.addEventListener("pageshow", alVolver)
    return () => {
      document.removeEventListener("visibilitychange", alVolver)
      window.removeEventListener("pageshow", alVolver)
      for (const reloj of mapa.values()) clearTimeout(reloj.timeout)
      mapa.clear()
    }
  }, [])
}

function armar(id, reloj) {
  reloj.timeout = setTimeout(() => {
    reloj.timeout = null
    toast.dismiss(id)
  }, Math.max(0, reloj.vence - Date.now()))
}

const GuardiaDeAvisos = ({ duracion }) => {
  useGuardiaDeAvisos(duracion)
  return null
}

const Toaster = ({
  style,
  ...props
}) => {
  const { theme = "system" } = useTheme()
  const hayBarra = useHayBarra()
  const ancho = useMedia(CONSULTA_ANCHO, true)
  const arriba = arribaDeLosAvisos(hayBarra, ancho)

  return (
    <>
    <GuardiaDeAvisos duracion={props.duration ?? props.toastOptions?.duration} />
    <Sonner
      theme={theme}
      offset={{ top: arriba }}
      mobileOffset={{ top: arriba, left: LADOS, right: LADOS }}
      icons={ICONOS_DE_LOS_AVISOS}
      className="toaster group"
      style={{ ...VARIABLES_DEL_AVISO, ...(hayBarra ? { clipPath: RECORTE_DEBAJO_DE_LA_BARRA } : null), ...style }}
      toastOptions={{
        style: ESTILO_DEL_AVISO,
        classNames: {
          toast: "group toast group-[.toaster]:shadow-lg",
          icon: "!w-[18px] !h-[18px]",
          description: "group-[.toast]:text-muted-foreground",
          actionButton:
            "group-[.toast]:bg-primary group-[.toast]:text-primary-foreground",
          cancelButton:
            "group-[.toast]:bg-muted group-[.toast]:text-muted-foreground",
        },
      }}
      {...props} />
    </>
  );
}

export { Toaster, toast }
